var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { lazy, memo, useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { animated, useTransition } from 'react-spring';
import cc from 'classcat';
// import {useSwipeEvents} from 'beautiful-react-hooks';
import { useHistory, useLocation, useParams } from 'react-router';
import { ReactTitle } from 'react-meta-tags';
import GA from 'react-ga';
import GA4 from 'react-ga4';
import ScreenSizes from '../../shared/screen-sizes';
import useSelectedNewspapers from '../../hooks/useSelectedNewspapers';
import { safeImgUrl, stringToMoment } from '../../shared/functions';
import Spinner from '../../components/Spinner/Spinner';
import TileNavigationButtons from '../../components/TileNavigationButtons';
import IconButton from '../../components/IconButton';
import ArrowBackIcon from '../../assets/icons/arrow-back.svg';
// import SwipeIcon from '../../assets/icons/swipe-icon.svg';
import useNewspaperByPublicationIdAndDate from '../../hooks/useNewspaperByPublicationIdAndDate';
import routesPaths from '../../routes/routesPaths';
import useAppGlobalData from '../../hooks/useAppGlobalData';
import NoData from '../../components/NoData/NoData';
import './newspaper-detail.style.scss';
var SidebarAd = lazy(function () { return import('../../components/Ads/SidebarAdv'); });
var NewspaperGridAd = lazy(function () { return import('../../components/Ads/NewspaperGridAd'); });
var getPageTitle = function (title, date) {
    return "\u03A0\u03C1\u03C9\u03C4\u03BF\u03C3\u03AD\u03BB\u03B9\u03B4\u03BF " + title + " - " + stringToMoment(date).format('yyyyMMDD');
};
/**
 * A button specifically for icons
 */
var NewspaperDetail = function (_a) {
    var _b;
    var className = _a.className;
    var _c = useParams(), publicationId = _c.publicationId, publicationSlug = _c.publicationSlug, date = _c.date;
    var _d = useSelectedNewspapers(), loading = _d[0], newspapers = _d[1];
    var _e = useNewspaperByPublicationIdAndDate(publicationId, date), currentNewspaperState = _e[0], fetchNewspaperByPublicationIdAndDate = _e[1].fetchNewspaperByPublicationIdAndDate;
    var currentNewspaper = currentNewspaperState.data;
    var _f = useAppGlobalData(), resetSearchCriteria = _f.resetSearchCriteria, selectedDate = _f.selectedDate;
    var history = useHistory();
    var location = useLocation();
    var imageRef = useRef(null);
    // const {onSwipeLeft, onSwipeRight} = useSwipeEvents(imageRef, {
    //   threshold: 150,
    //   preventDefault: false,
    // });
    // const [showSwipeIcon, setShowSwipeIcon] = useState(true);
    var navigationRef = useRef(null);
    var _g = useState({
        '--navigation-top': '50%',
    }), navigationTopProperty = _g[0], setNavigationTopProperty = _g[1];
    var _h = useState(false), imgLoaded = _h[0], setImgLoaded = _h[1];
    var isApp = useMemo(function () {
        var query = new URLSearchParams(location.search);
        var appParam = query.get('sport24app');
        return appParam === 'true';
    }, [location]);
    // Takes care of always showing navigation buttons for every viewport height
    // Calculate the correct height once we are sure that the image has loaded
    useEffect(function () {
        var _a;
        if (imgLoaded && navigationRef.current && imageRef.current) {
            var navigationTop = navigationRef.current.getBoundingClientRect().top;
            // Calculate how far from viewport bottom navigation buttons should at least be
            var threshold = window.innerHeight - 100 - navigationRef.current.clientHeight;
            if (navigationTop && navigationTop >= threshold) {
                var diff = navigationTop - threshold;
                var imageTop = (_a = imageRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect().top;
                // Update navigation buttons top by subtracting difference with threshold, also taking into account
                // that the top is calculated in css from the image top.
                setNavigationTopProperty({
                    '--navigation-top': navigationTop - diff - imageTop + "px",
                });
            }
        }
    }, [imgLoaded]);
    var fadeAnimationProps = useMemo(function () { return ({
        from: {
            opacity: 0,
        },
        enter: {
            opacity: 1,
        },
        leave: {
            opacity: 0,
        },
        config: { duration: 200 },
    }); }, []);
    // Back to home does same as reset button:
    // Resets search criteria and returns to main page with daily view (remembering selected date)
    var backToHome = useCallback(function () {
        resetSearchCriteria();
        if (!isApp) {
            history.push(routesPaths.newspaperPageBase + "/date/" + selectedDate + "/");
        }
        else
            history.push(routesPaths.newspaperPageBase + "/date/" + selectedDate + "/?sport24app=true");
    }, [history, resetSearchCriteria, routesPaths, isApp, selectedDate]);
    // Changes the shown newspaper to the previous or next newspaper, using selected newspapers array
    // Also updates the url with the new newspaper data
    var navigate = useCallback(function (navigation) {
        // If navigating through a daily view, we can identify a newspaper by its publicationId.
        // If navigating through a monthly view, we can identify a newspaper by its date.
        var currentNewspaperIndex = newspapers.findIndex(function (paper) {
            return paper.publicationId
                ? paper.publicationId === (currentNewspaper === null || currentNewspaper === void 0 ? void 0 : currentNewspaper.publicationId)
                : paper.datePublished === (currentNewspaper === null || currentNewspaper === void 0 ? void 0 : currentNewspaper.datePublished);
        });
        var nextNewspaper = newspapers[currentNewspaperIndex + navigation];
        if (nextNewspaper) {
            // Set params needed in url based on if we are navigating through a daily view or through a monthly view
            var newSlug = nextNewspaper.name || publicationSlug;
            var newDate = nextNewspaper.datePublished || date;
            var newId = nextNewspaper.publicationId || publicationId;
            if (!isApp)
                history.push(routesPaths.newspaperPageBase + "/protoselido-" + newSlug + "/" + newDate + "/" + newId + "/");
            else
                history.push(routesPaths.newspaperPageBase + "/protoselido-" + newSlug + "/" + newDate + "/" + newId + "/?sport24app=true");
        }
    }, [newspapers, currentNewspaper, date, isApp, history]);
    // Show swipe icon for 1,5 second on mount
    // useEffect(() => {
    //   const timeout = setTimeout(() => setShowSwipeIcon(false), 1500);
    //   return () => clearTimeout(timeout);
    // }, []);
    useEffect(function () {
        setImgLoaded(false);
    }, [currentNewspaper]);
    // Checks if the newspaper with the id in the url already exists
    // (this will only be the case when user enters the site from the detail view, or refreshes detail view).
    // If it doesn't exist, fetch the newspaper.
    useEffect(function () {
        if (!currentNewspaper) {
            fetchNewspaperByPublicationIdAndDate(publicationId, stringToMoment(date));
        }
    }, [publicationId, date]);
    /**
     * Google Analytics
     */
    useEffect(function () {
        var _a;
        // fire pageview only if currentNewspaper is available (this happens after location change, so location is up-to-date here)
        if (currentNewspaper) {
            GA.initialize('UA-482127-1');
            GA.ga('send', 'pageview', {
                page: "" + location.pathname + location.search,
                title: currentNewspaper && currentNewspaper.title
                    ? getPageTitle(currentNewspaper.title, (_a = currentNewspaper.datePublished) !== null && _a !== void 0 ? _a : date)
                    : document.title,
            });
        }
    }, [currentNewspaper]); // currentNewspaper is only updated by URL Params (see lines 50-60) so this only runs when location updates
    /**
     * Google Analytics 4
     */
    useEffect(function () {
        var _a;
        // fire pageview only if currentNewspaper is available (this happens after location change, so location is up-to-date here)
        if (currentNewspaper) {
            GA4.initialize([
                {
                    trackingId: 'G-CBFPZXDS6N',
                },
                {
                    trackingId: 'G-NTXG7V0NM2',
                },
            ]);
            GA4.ga('send', 'pageview', {
                page: "" + location.pathname + location.search,
                title: currentNewspaper && currentNewspaper.title
                    ? getPageTitle(currentNewspaper.title, (_a = currentNewspaper.datePublished) !== null && _a !== void 0 ? _a : date)
                    : document.title,
            });
        }
    }, [currentNewspaper]); // currentNewspaper is only updated by URL Params (see lines 50-60) so this only runs when location updates
    // onSwipeLeft(() => {
    //   navigate(1);
    // });
    // onSwipeRight(() => {
    //   navigate(-1);
    // });
    var imageTransition = useTransition(currentNewspaper, __assign(__assign({}, fadeAnimationProps), { from: __assign(__assign({}, fadeAnimationProps.from), { gridRowStart: 1, gridColumnStart: 1 }) }));
    // const swipeIconTransition = useTransition(showSwipeIcon, fadeAnimationProps);
    return (React.createElement("div", { className: cc([className, 'tfm-newspaper-detail']) },
        currentNewspaper && (React.createElement(ReactTitle, { title: getPageTitle(currentNewspaper.title, (_b = currentNewspaper.datePublished) !== null && _b !== void 0 ? _b : date) })),
        React.createElement("div", { className: "tfm-newspaper-detail-container" },
            loading && React.createElement(Spinner, { className: "tfm-newspaper-detail-spinner" }),
            React.createElement("div", { className: "tfm-newspaper-detail-image-container", ref: imageRef, style: navigationTopProperty },
                !loading && !currentNewspaper && (React.createElement(NoData, { className: "tfm-newspaper-detail-no-data", text: "\u0394\u03B5\u03BD \u03C5\u03C0\u03AC\u03C1\u03C7\u03B5\u03B9 \u03C0\u03C1\u03C9\u03C4\u03BF\u03C3\u03AD\u03BB\u03B9\u03B4\u03BF" })),
                imageTransition(function (styles, newspaper) { return (React.createElement(React.Fragment, null, newspaper && (React.createElement(animated.img, { style: styles, key: newspaper.publicationId || newspaper.datePublished, className: "tfm-newspaper-detail-cover", srcSet: "\n                    " + (newspaper.imgSmallUrl ? safeImgUrl(newspaper.imgSmallUrl) + " 380w," : '') + "\n                    " + safeImgUrl(newspaper.imgUrl) + " 853w\n                  ", sizes: "\n                    (max-width: " + ScreenSizes.Mobile + ") 380px,\n                    853px\n                  ", src: safeImgUrl(newspaper.imgUrl), alt: "\u0395\u03BE\u03CE\u03C6\u03C5\u03BB\u03BB\u03BF \u03B5\u03C6\u03B7\u03BC\u03B5\u03C1\u03AF\u03B4\u03B1\u03C2 \u03BC\u03B5 \u03B4\u03C5\u03BD\u03B1\u03C4\u03CC\u03C4\u03B7\u03C4\u03B1 \u03BA\u03BB\u03B9\u03BA " + newspaper.title, onLoad: function () { return setImgLoaded(true); } })))); }),
                React.createElement(TileNavigationButtons, { navRef: navigationRef, className: "tfm-newspaper-detail-navigation", navigateLeft: function () { return navigate(-1); }, navigateRight: function () { return navigate(1); }, leftDisabled: newspapers.length === 0 || Object.is(newspapers[0], currentNewspaper), rightDisabled: newspapers.length === 0 ||
                        Object.is(newspapers[newspapers.length - 1], currentNewspaper) })),
            React.createElement(IconButton, { className: "tfm-newspaper-detail-back", "aria-label": "\u0395\u03C0\u03B9\u03C3\u03C4\u03C1\u03AD\u03C8\u03C4\u03B5 \u03C3\u03C4\u03B7\u03BD \u03B5\u03C0\u03B9\u03C3\u03BA\u03CC\u03C0\u03B7\u03C3\u03B7 \u03C4\u03B7\u03C2 \u03B5\u03C6\u03B7\u03BC\u03B5\u03C1\u03AF\u03B4\u03B1\u03C2", onClick: backToHome },
                React.createElement(ArrowBackIcon, null)),
            React.createElement(NewspaperGridAd, { className: "tfm-newspaper-detail-ad" })),
        React.createElement(SidebarAd, { className: "tfm-newspaper-detail-sidebar-ad" })));
};
export default memo(NewspaperDetail);
